import { UUID } from "./uuid.js";

// -------------------------
// Main entry point
// -------------------------
export async function uploadFile(dataURL, filename) {
  const file = dataURLToFile(dataURL, filename);
  const token = await getToken();
  const uuid = UUID.generate();

  return await initUpload(token, file, uuid);
}

window.uploadFile = uploadFile;

// -------------------------
// Convert Data URL to File
// -------------------------
function dataURLToFile(dataURL, filename) {
  const [header, base64] = dataURL.split(',');
  const mime = header.match(/:(.*?);/)[1];  
  const bstr = atob(base64);
  const n = bstr.length;
  const u8arr = new Uint8Array(n);

  for (let i = 0; i < n; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }

  return new File([u8arr], filename, { type: mime });
}

// -------------------------
// Fetch token
// -------------------------
async function getToken() {
  const response = await fetch('https://brotransfer.com/sistem/PagesAjax/PagesAjaxGetToken.php', {
    method: 'POST'
  });

  if (!response.ok) throw new Error("Failed to fetch token");
  const data = await response.json();
  return data.token;
}

// -------------------------
// Refresh token (returns Promise)
// -------------------------
async function refreshToken() {
  return await getToken();
}

// -------------------------
// Init upload
// -------------------------
async function initUpload(token, file, uuid) {
  if (file.size < 8 * 1024 * 1024) {
    // small file upload
    return await uploadSmallFile(token, file, uuid);
  } else {
    // chunked upload
    return await uploadChunkedFile(token, file, uuid);
  }
}

// -------------------------
// Small file upload
// -------------------------
async function uploadSmallFile(token, file, uuid) {
  const ext = file.name.split('.').pop();
  const fname = UUID.generate() + "." + ext;

  const response = await fetch('https://content.dropboxapi.com/2/files/upload', {
    method: 'POST',
    headers: {
      "Authorization": `Bearer ${token}`,
      "Dropbox-API-Arg": JSON.stringify({
        autorename: true,
        mode: "add",
        mute: false,
        path: `/posterx/${fname}`,
        strict_conflict: false
      }),
      "Content-Type": "application/octet-stream"
    },
    body: file
  });

  if (!response.ok) {
    if (response.status === 401) {
      const newToken = await refreshToken();
      return await uploadSmallFile(newToken, file, uuid);
    }
    throw new Error(`Upload failed with status ${response.status}`);
  }

  const data = await response.json();
  const downloadLink = await createSharedLink(token, data.path_lower);

  return {
    originalName: file.name,
    fileName: data.name,
    boxId: data.id,
    refId: data.id, //uuid,
    path: data.path_lower,
    downloadLink
  };
}

// -------------------------
// Chunked file upload
// -------------------------
async function uploadChunkedFile(token, file, uuid) {
  const chunkSize = 8 * 1024 * 1024;
  const fileSize = file.size;
  let offset = 0;
  let sessionId = "";

  while (offset < fileSize) {
    const start = offset;
    const end = Math.min(offset + chunkSize, fileSize);
    const filePart = await readFilePart(file, start, end);

    if (offset === 0) {
      sessionId = await startSession(token, filePart);
    } else if (end < fileSize) {
      await appendFile(token, filePart, offset, sessionId);
    } else {
      return await finishSession(token, filePart, offset, file.name, sessionId, uuid);
    }

    offset += chunkSize;
  }
}

// -------------------------
// Dropbox upload session functions
// -------------------------
async function startSession(token, filePart, retries = 0) {
  const response = await fetch("https://content.dropboxapi.com/2/files/upload_session/start", {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Dropbox-API-Arg": JSON.stringify({ close: false }),
      "Content-Type": "application/octet-stream"
    },
    body: filePart
  });

  if (!response.ok) {
    if (response.status === 401 && retries < 3) {
      const newToken = await refreshToken();
      return await startSession(newToken, filePart, retries + 1);
    }
    throw new Error(`Start session failed with status ${response.status}`);
  }

  const data = await response.json();
  return data.session_id;
}

async function appendFile(token, filePart, offset, sessionId, retries = 0) {
  const response = await fetch("https://content.dropboxapi.com/2/files/upload_session/append_v2", {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Dropbox-API-Arg": JSON.stringify({
        cursor: { offset, session_id: sessionId },
        close: false
      }),
      "Content-Type": "application/octet-stream"
    },
    body: filePart
  });

  if (!response.ok) {
    if (response.status === 401 && retries < 3) {
      const newToken = await refreshToken();
      return await appendFile(newToken, filePart, offset, sessionId, retries + 1);
    }
    throw new Error(`Append file failed with status ${response.status}`);
  }

  try { await response.json(); } catch { /* Dropbox returns empty 200 */ }
}

async function finishSession(token, filePart, offset, fileName, sessionId, uuid, retries = 0) {
  const ext = fileName.split('.').pop();
  const fname = UUID.generate() + "." + ext;

  const response = await fetch("https://content.dropboxapi.com/2/files/upload_session/finish", {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Dropbox-API-Arg": JSON.stringify({
        commit: {
          autorename: true,
          mode: "add",
          mute: false,
          path: `/posterx/${fname}`,
          strict_conflict: false
        },
        cursor: { offset, session_id: sessionId }
      }),
      "Content-Type": "application/octet-stream"
    },
    body: filePart
  });

  if (!response.ok) {
    if (response.status === 401 && retries < 3) {
      const newToken = await refreshToken();
      return await finishSession(newToken, filePart, offset, fileName, sessionId, uuid, retries + 1);
    }
    throw new Error(`Finish session failed with status ${response.status}`);
  }

  const data = await response.json();
  const downloadLink = await createSharedLink(token, data.path_lower);

  return {
    originalName: fileName,
    fileName: data.name,
    boxId: data.id,
    refId: uuid,
    path: data.path_lower,
    downloadLink
  };
}

// -------------------------
// Create Dropbox shared link
// -------------------------
async function createSharedLink(token, path, retries = 0) {
  const request = {
    path: path,
    settings: { requested_visibility: "public" }
  };

  const response = await fetch("https://api.dropboxapi.com/2/sharing/create_shared_link_with_settings", {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(request)
  });

  if (!response.ok) {
    if (response.status === 401 && retries < 3) {
      const newToken = await refreshToken();
      return await createSharedLink(newToken, path, retries + 1);
    }
    throw new Error(`Create shared link failed with status ${response.status}`);
  }

  const data = await response.json();
  return data.url.replace("dl=0", "dl=1");
}

// -------------------------
// Read file slice
// -------------------------
function readFilePart(file, start, end) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read file part"));
    reader.readAsArrayBuffer(file.slice(start, end));
  });
}
